import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../../../environments/environment';
import { Store } from '@ngrx/store';
import {
  AuthenticationDataModel,
  AuthenticationNgRxActions,
} from '@geekbears/gb-ngrx-auth-basic';
import { AnalyticsService, appAuthenticatedUserSelector } from '@core/application';
import { filter, take } from 'rxjs/operators';
import { APP_ROUTES } from '@typings/app-routes.constant';
import { passwordValidator, getPasswordValidationMessages } from '@utils/password.validator';


@Component({
  selector: 'app-account-information',
  templateUrl: './account-information.component.html',
  styleUrls: ['./account-information.component.scss']
})
export class AccountInformationComponent implements OnInit {
  readonly fb: FormBuilder;
  readonly http: HttpClient;
  readonly router: Router;

  accountForm!: FormGroup;
  isLoading = false;
  errorMessage: string | null = null;
  organizationData: any = null;
  registerEmail: string = '';
  deviceInfo: any = null;

  constructor(
    fb: FormBuilder,
    http: HttpClient,
    router: Router,
    private readonly store$: Store,
    private readonly analytics: AnalyticsService
  ) {
    this.fb = fb;
    this.http = http;
    this.router = router;
    this.captureDeviceInfo();
  }

  ngOnInit(): void {
    this.retrieveStoredData();
    this.initializeForm();
  }

  private captureDeviceInfo(): void {
    // Capture device information (name and a unique ID)
    this.deviceInfo = {
      id: this.generateDeviceId(),
      name: this.getDeviceName(),
      platform:'web'
    };
  }

  private generateDeviceId(): string {
    // Generate or retrieve a unique device ID
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = 'device-' + Math.random().toString(36).substring(2, 11);
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  private getDeviceName(): string {
    // Get browser/device name from user agent
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) return 'Chrome Browser';
    if (userAgent.includes('Safari')) return 'Safari Browser';
    if (userAgent.includes('Firefox')) return 'Firefox Browser';
    if (userAgent.includes('MSIE') || userAgent.includes('Trident')) return 'Internet Explorer';
    return 'Unknown Device';
  }

  retrieveStoredData(): void {
    // Retrieve email from register flow
    const email = localStorage.getItem('pendingRegisterEmail');
    this.registerEmail = email || '';

    // Retrieve organization info from organization-information page
    const orgInfoString = localStorage.getItem('pendingOrganizationInfo');
    if (orgInfoString) {
      try {
        this.organizationData = JSON.parse(orgInfoString);
      } catch (e) {
        // console.error('Failed to parse organization data:', e);
        this.organizationData = null;
      }
    }

    // If no data, redirect back to organization information
    if (!this.organizationData || !this.registerEmail) {
      this.router.navigate(['/authentication/organization-information']);
    }
  }

  initializeForm(): void {
    this.accountForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      password: ['', [Validators.required, passwordValidator()]],
      confirmPassword: ['', [Validators.required, passwordValidator()]],
    });
  }

  getPasswordErrorMessage(controlName: string): string {
    const control = this.accountForm?.get(controlName);
    if (!control || !control.touched || control.valid) {
      return '';
    }
    if (control.errors?.['required']) {
      return 'Please enter your password';
    }
    if (control.errors?.['passwordStrength']?.firstMessage) {
      return control.errors['passwordStrength'].firstMessage;
    }
    const msgs = getPasswordValidationMessages(control.value || '');
    return msgs.length > 0 ? msgs[0] : '';
  }

  createAccount(): void {
    if (this.accountForm.invalid) {
      this.errorMessage = 'Please fill in all required fields correctly.';
      return;
    }

    // Validate password match
    const password = this.accountForm.get('password')?.value;
    const confirmPassword = this.accountForm.get('confirmPassword')?.value;
    if (password !== confirmPassword) {
      this.errorMessage = 'Passwords do not match.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    console.debug('[AccountInformation] Starting createAccount, payload preview:', {
      email: this.registerEmail,
      firstName: this.accountForm.get('firstName')?.value,
      lastName: this.accountForm.get('lastName')?.value,
      companyName: this.organizationData?.organizationName,
      industry: this.organizationData?.industry,
    });

    // Build payload according to API specification
    const signUpPayload = {
      firstName: this.accountForm.get('firstName')?.value,
      lastName: this.accountForm.get('lastName')?.value,
      companyName: this.organizationData?.organizationName || '',
      email: this.registerEmail,
      password: this.accountForm.get('password')?.value,
      confirmPassword: this.accountForm.get('confirmPassword')?.value,
    //   address: {
    //     city: this.organizationData?.city || '',
    //     state: this.organizationData?.state || '',
    //     zip: this.organizationData?.zipCode || '',
    //     country: this.organizationData?.country || ''
    //   },
    //   industry: this.organizationData?.industry || '',
      deviceInfo: this.deviceInfo
    };

    this.http.post<any>(`${environment.apiDomain}/v3/authentication/sign-up`, signUpPayload)
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          console.debug('[AccountInformation] Sign-up response:', response);

          // Backend confirmed account creation.
          this.analytics.trackSignUp();
          
          // Some backends return tokens at top level and user under data.user.
          const user = response?.data?.user ?? response?.data ?? response?.user;
          const authToken = response?.authToken ?? response?.data?.authToken ?? response?.data?.token;
          const refreshToken = response?.refreshToken ?? response?.data?.refreshToken;

          if (user) {
            localStorage.setItem('s_user', JSON.stringify(user));
            console.debug('[AccountInformation] stored s_user');
          }

          if (authToken) {
            localStorage.setItem('auth_token', authToken);
            console.debug('[AccountInformation] stored auth token');
          }

          if (refreshToken) {
            localStorage.setItem('refresh_token', refreshToken);
            console.debug('[AccountInformation] stored refresh token');
          }

          // store confirm token if present on user record
          const confirmToken = user?.confirmAccountToken ?? response?.data?.confirmAccountToken ?? response?.confirmAccountToken;
          if (confirmToken) {
            localStorage.setItem('confirmAccountToken', confirmToken);
            console.debug('[AccountInformation] stored confirmAccountToken:', confirmToken);
          }

          // Dispatch auth session to populate authenticated user in store
          const userId = user?.id ?? user?._id ?? response?.data?.id;
          if (authToken && userId) {
            console.debug('[AccountInformation] dispatching setAuthSession', { userId, authToken });
            this.store$.dispatch(
              AuthenticationNgRxActions.setAuthSession({
                data: new AuthenticationDataModel({
                  id: userId,
                  token: authToken,
                  refreshToken: refreshToken,
                }),
              })
            );

            // wait until the authenticated user is reflected in the store
            this.store$
              .select(appAuthenticatedUserSelector)
              .pipe(
                filter((u) => !!u),
                take(1)
              )
              .subscribe((u) => {
                console.debug('[AccountInformation] appAuthenticatedUserSelector now has user', u);
                // Clear temporary registration data
                localStorage.removeItem('pendingRegisterEmail');
                localStorage.removeItem('pendingOrganizationInfo');

                // Navigate to confirm-account page for OTP verification
                 this.router.navigateByUrl(
                              APP_ROUTES.authentication['confirm-account'].fullPath
                            );
              });
            return;
          }

          // If we don't have tokens/user to populate auth store, fallback to
          // storing values and navigating directly.
          localStorage.removeItem('pendingRegisterEmail');
          localStorage.removeItem('pendingOrganizationInfo');
           this.router.navigateByUrl(
              APP_ROUTES.authentication['confirm-account'].fullPath
            );
        },
        error: (error) => {
          this.isLoading = false;
          // console.error('Sign-up failed:', error);
          this.errorMessage = error?.error?.message || 'Sign-up failed. Please try again.';
        }
      });
  }

  back(): void {
    this.router.navigate(['/authentication/organization-information']);
  }
}
